
import { verifyAliyunCaptcha } from "@/utils/aliyunCaptcha";
import { normalizeLocale } from "@/utils/locale";

const EMAIL_REG = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_COOLDOWN = 60; // 发码冷却（秒）
const MAIN_SITE_SUCCESS_CODE = 1; // 总站 passport / deletion 成功码

export default {
  name: "AppDeleteAccount",
  layout: "app",
  head() {
    return {
      title: this.$t("page.DELETE_ACCOUNT"),
      meta: [
        {
          hid: "description",
          name: "description",
          content: this.$t("page.DELETE_ACCOUNT_META"),
        },
        {
          name: "viewport",
          content:
            "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
        },
      ],
    };
  },
  data() {
    return {
      step: 1,
      agreed: false,
      submitting: false,
      sendCodeLoading: false,
      codeCounting: false,
      codeCountdown: 0,
      codeTimer: null,
      emailError: "",
      form: {
        email: "",
        code: "",
      },
    };
  },
  computed: {
    requestLang() {
      return normalizeLocale(this.$i18n.locale);
    },
    canSubmit() {
      if (this.step === 1) {
        return this.agreed;
      }
      if (this.step === 2) {
        return EMAIL_REG.test(this.form.email) && this.form.code.length === 6;
      }
      return false;
    },
    primaryBtnText() {
      if (this.step === 1) {
        return this.$t("page.SUBMIT_APPLICATION");
      }
      return this.$t("page.CONFIRM");
    },
    codeBtnText() {
      if (this.codeCounting) {
        return `${this.codeCountdown}s`;
      }
      return this.$t("page.GET_CODE");
    },
  },
  beforeDestroy() {
    this.clearCodeTimer();
  },
  methods: {
    /** 第 2 步回到协议；其余离开注销页。申请成功后不再回到表单 */
    goBack() {
      if (this.step === 2) {
        this.step = 1;
        return;
      }
      if (process.client && window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.replace("/");
    },
    async handlePrimaryAction() {
      if (!this.canSubmit || this.submitting) {
        return;
      }
      if (this.step === 1) {
        this.step = 2;
        return;
      }
      if (this.step === 2) {
        await this.submitVerification();
      }
    },
    /** 验证码只保留 6 位数字，避免带空格或字母提交 */
    onCodeInput(value) {
      this.form.code = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 6);
    },
    /** 仅校验邮箱格式；是否已注册由发码 / 注销接口判断 */
    validateEmail() {
      if (!this.form.email) {
        this.emailError = this.$t("page.ENTER_YOUR_EMAIL");
        return false;
      }
      if (!EMAIL_REG.test(this.form.email)) {
        this.emailError = this.$t("page.ENTER_VALID_EMAIL");
        return false;
      }
      this.emailError = "";
      return true;
    },
    /** 格式通过后走阿里云验证码并发送注销邮件验证码 */
    async handleGetCode() {
      if (this.codeCounting || this.sendCodeLoading) {
        return;
      }
      if (!this.validateEmail()) {
        return;
      }

      this.sendCodeLoading = true;
      try {
        const sceneRes = await this.$global.getSceneId();
        if (sceneRes?.code !== MAIN_SITE_SUCCESS_CODE) {
          this.$message.error(
            sceneRes?.msg || this.$t("page.GET_VERIFICATION_SCENE_FAILED")
          );
          return;
        }
        const encryptedSceneId = sceneRes?.data?.encrypted_scene_id;
        if (!encryptedSceneId) {
          this.$message.error(this.$t("page.GET_VERIFICATION_SCENE_FAILED"));
          return;
        }

        const captchaResult = await verifyAliyunCaptcha({
          EncryptedSceneId: encryptedSceneId,
          language: this.requestLang,
        });
        if (!captchaResult) {
          this.$message.error(this.$t("page.VERIFICATION_FAILED"));
          return;
        }

        const res = await this.$global.sendDeletionCode(
          {
            email: this.form.email,
            captcha: captchaResult,
          },
          {
            lang: this.requestLang,
          }
        );
        if (res?.code === MAIN_SITE_SUCCESS_CODE) {
          this.$message.success(res?.msg || this.$t("page.CODE_SENT"));
          this.startCodeCountdown();
          return;
        }
        this.$message.error(res?.msg || this.$t("page.SEND_FAILED"));
      } catch (error) {
        if (error?.message === "Captcha closed by user") {
          return;
        }
        this.$message.error(error?.message || this.$t("page.SEND_FAILED"));
      } finally {
        this.sendCodeLoading = false;
      }
    },
    startCodeCountdown() {
      this.clearCodeTimer();
      this.codeCounting = true;
      this.codeCountdown = CODE_COOLDOWN;
      this.codeTimer = setInterval(() => {
        this.codeCountdown -= 1;
        if (this.codeCountdown <= 0) {
          this.clearCodeTimer();
        }
      }, 1000);
    },
    clearCodeTimer() {
      if (this.codeTimer) {
        clearInterval(this.codeTimer);
        this.codeTimer = null;
      }
      this.codeCounting = false;
      this.codeCountdown = 0;
    },
    /** 二次确认后提交注销申请；成功码只认数字 1 */
    async submitVerification() {
      if (this.submitting) {
        return;
      }
      if (!this.validateEmail()) {
        return;
      }
      if (this.form.code.length !== 6) {
        this.$message.warning(this.$t("page.INPUT_6DIGIT_CODE"));
        return;
      }

      try {
        await this.$confirm(
          this.$t("page.DELETE_ACCOUNT_CONFIRM"),
          this.$t("page.DELETE_ACCOUNT"),
          {
            confirmButtonText: this.$t("page.CONFIRM"),
            cancelButtonText: this.$t("page.CANCEL"),
            type: "warning",
          }
        );
      } catch (error) {
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$global.requestDeletion(
          {
            email: this.form.email,
            verif_code: this.form.code,
          },
          {
            lang: this.requestLang,
          }
        );
        if (res?.code === MAIN_SITE_SUCCESS_CODE) {
          this.step = 3;
          return;
        }
        this.$message.error(res?.msg || this.$t("page.UNKNOWN_ERROR"));
      } catch (error) {
        this.$message.error(error?.message || this.$t("page.NETWORK_ERROR"));
      } finally {
        this.submitting = false;
      }
    },
  },
};
